import Path from '@leverege/path'

const SPLIT = /[/.]/

/**
 * Creates a function that will shallow copy the model and set the
 * key to an empty oject.
 * @param {string} key the name of the field
 * @param {object} [options]
 * @param {function} [options.validate] ( model, key ) function is called to
 * allow it to reject the change by throwing an error
 **/
function createClearMap( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model ) => {
      if ( options && options.validate ) {
        options.validate( model, key )
      }

      return path.copy( model, {} )
    }
  }

  return ( model ) => {
    if ( options && options.validate ) {
      options.validate( model, key )
    }

    const c = { ...model }
    c[key] = {}
    return c
  }
}

/**
 * Creates a function that will shallow copy the model and set the
 * object to an given object.
 * @param {string} key the name of the field
 * @param {object} [options]
 * @param {boolean} [options.copy] if true, a new array will be made and the values
 * in array will be copied into it
 * @param {function} [options.validate] ( model, key ) function is called to
 * allow it to reject the change by throwing an error
 **/
function createSetMap( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, object ) => {
      if ( object == null || Array.isArray( object ) || typeof object !== 'object' ) {
        throw new Error( 'SetObject must be given an object' )
      }

      if ( options && options.validate ) {
        options.validate( model, key, object )
      }

      const cValue = path.get( model )
      if ( object === cValue ) {
        return model
      }

      return path.copy(
        model,
        options && options.copy === true ? { ...object } : object
      )
    }
  }

  return ( model, object ) => {
    if ( object == null || Array.isArray( object ) || typeof object !== 'object' ) {
      throw new Error( 'SetObject must be given an object' )
    }
    if ( options && options.validate ) {
      options.validate( model, key, object )
    }
    const c = { ...model }
    c[key] = options && options.copy === true ? { ...object } : object
    return c
  }
}

/**
 * Creates a shallow copy of the model, and sets the item at the
 * specified itemKey. If itemKeu is out of null, an error is thrown
 * @param {string} key the name of the field
 * @param {object} options 
 * @param {function} [options.validate] ( model, key, item, index ) function is called to
 * allow it to reject the change by throwing an error
 **/
function createSetMapKey( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, itemKey, item ) => {
      if ( itemKey == null ) {
        throw new Error( 'Replace Item Key is null or undefined' )
      }

      if ( options && options.validate ) {
        options.validate( model, key, item, itemKey )
      }

      if ( path.get( model ) && path.get( model )[itemKey] === item ) {
        return model
      }

      return path.copy( model, { ...path.get( model ), [itemKey] : item } )
    }
  }

  return ( model, itemKey, item ) => {
    
    if ( itemKey == null ) {
      throw new Error( 'Replace Item Key is null or undefined' )
    }
  
    if ( options && options.validate ) {
      options.validate( model, key, item, itemKey )
    }
    if ( model[key] && model[key][itemKey] === item ) {
      return model
    }
    const c = { ...model }
    c[key] = { ...model[key], [itemKey] : item }
    return c
  }
}

/**
 * Returns a function that returns the map
 * @param {string} key the name of the field
 * @param {object} [options]
 * @param {function} [options.copy] if true, a copy of the array is returned
 **/
function createGetMap( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )

    return ( model ) => {
      if ( options && options.copy === true ) {
        return path.get( model ) == null ? { } : { ...path.get( model ) }
      }

      return path.get( model ) == null ? { } : path.get( model )
    }
  }

  return ( model ) => {
    if ( options && options.copy === true ) {
      return model?.[key] == null ? { } : { ...model[key] }
    }
    return model?.[key] == null ? { } : model[key]
  }
}

/**
 * Returns a function that returns the array of [key,value] in the map
 * @param {string} key the name of the field
 * @param {object} [options]
 **/
function createGetMapEntries( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )

    return ( model ) => {
      return path.get( model ) == null ? [] : Object.entries( path.get( model ) )
    }
  }

  return ( model ) => {
    return model?.[key] == null ? [] : Object.entries( model[key] )
  }
}

/**
 * Returns a function that returns the item at the itemKey
 * @param {string} key the name of the field
 **/
function createGetMapKey( key ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )

    return ( model, itemKey ) => {
      return path.get( model ) == null ? undefined : path.get( model )[itemKey]
    }
  }

  return ( model, itemKey ) => {
    return model?.[key] == null ? undefined : model[key][itemKey]
  }
}

/**
 * Returns a function that returns the index of item in the array
 * @param {string} key the name of the field
**/
function createContainsMapKey( key ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )

    return ( model, itemKey ) => {
      return path.get( model ) == null ? false : path.get( model )[itemKey] !== undefined
    }
  }

  return ( model, itemKey ) => {
    return model[key] == null ? false : model[key][itemKey] !== undefined
  }
}

/**
 * Creates a shallow copy of the model, and removes the item.
 * @param {string} key the name of the array field
 * @param {object} [options]
 * @param {function} [options.validate] ( model, key, item, index ) function is called to
 * allow it to reject the change by throwing an error
 **/
function createRemoveMapKey( key, options ) {
  if ( key.includes( '/' ) || key.includes( '.' ) ) {
    const path = Path( key.split( SPLIT ) )
    return ( model, itemKey ) => {
      if ( path.get( model ) == null || path.get( model )[itemKey] === undefined ) {
        return model
      }

      if ( options && options.validate ) {
        options.validate( model, key, path.get( model )[itemKey], itemKey )
      }

      const c = path.copy( model, { ...path.get( model ) } )
      const val = path.get( c )
      delete val[itemKey]

      return c
    }
  }

  return ( model, itemKey ) => {
    if ( model[key] == null || model[key][itemKey] === undefined ) {
      return model
    }

    if ( options && options.validate ) {
      options.validate( model, key, model[key][itemKey], itemKey )
    }
    
    const c = { ...model }
    c[key] = { ...model[key] }
    delete c[key][itemKey]
    return c
  }
}

function createAllMap( key, name, namePlural ) {
  if ( name == null ) {
    if ( key[key.length - 1] === 's' ) {
      // eslint-disable-next-line no-param-reassign
      name = `${key[0].toUpperCase()}${key.substring( 1, key.length - 1 )}`
    } else {
      // eslint-disable-next-line no-param-reassign
      name = `${key[0].toUpperCase()}${key.substring( 1 )}`
    }
  } else {
    // eslint-disable-next-line no-param-reassign
    name = `${name[0].toUpperCase() + name.substring( 1 )}`
  }

  if ( namePlural == null ) {
    if ( key[key.length - 1] === 's' ) {
      // eslint-disable-next-line no-param-reassign
      namePlural = `${key[0].toUpperCase() + key.substring( 1 )}`
    } else {
      // eslint-disable-next-line no-param-reassign
      namePlural = `${key[0].toUpperCase() + key.substring( 1 )}s`
    }
  } else {
    // eslint-disable-next-line no-param-reassign
    namePlural = `${namePlural[0].toUpperCase() + namePlural.substring( 1 )}`
  }
  
  const methods = {
    [`set${name}`] : createSetMapKey( key ),
    [`get${name}`] : createGetMapKey( key ),
    [`contains${name}`] : createContainsMapKey( key ),
    [`remove${name}`] : createRemoveMapKey( key ),
    [`set${namePlural}`] : createSetMap( key ),
    [`get${namePlural}`] : createGetMap( key ),
    [`get${name}Entries`] : createGetMapEntries( key ),
    [`clear${namePlural}`] : createClearMap( key ),
  }

  const normalized = {
    set : methods[`set${name}`],
    get : methods[`get${name}`],
    contains : methods[`contains${name}`],
    remove : methods[`remove${name}`],
    setAll : methods[`set${namePlural}`],
    getAll : methods[`get${namePlural}`],
    entries : methods[`get${name}Entries`],
    clear : methods[`clear${namePlural}`],
  }
  methods[`${namePlural[0].toLowerCase() + namePlural.substring( 1 )}`] = ( ) => normalized
  return methods
}

export {
  createSetMapKey,
  createGetMapKey,
  createContainsMapKey,
  createRemoveMapKey,
  createSetMap,
  createGetMap,
  createGetMapEntries,
  createClearMap,
  createAllMap
}
